import * as Sentry from "@sentry/vue";

export const USER_CAUSED_HTTP_STATUSES = new Set([
  400,
  401,
  403,
  404,
  405,
  409,
  410,
  412,
  413,
  415,
  422,
  429,
]);

const IGNORED_AXIOS_ERROR_CODES = new Set([
  "ERR_CANCELED",
]);

export const shouldCaptureAxiosError = (error) => {
  if (IGNORED_AXIOS_ERROR_CODES.has(error?.code)) return false;

  const status = error?.response?.status;

  // Ignore transport-level failures that never produced an HTTP response.
  if (!status) return false;
  if (status >= 500) return true;
  if (status < 400 || status >= 500) return false;

  return !USER_CAUSED_HTTP_STATUSES.has(status);
};

const buildAxiosContext = (error) => {
  const status = error?.response?.status ?? null;
  const method = error?.config?.method?.toUpperCase?.() ?? null;
  const url = error?.config?.url ?? null;
  const responseData = error?.response?.data;

  return {
    status,
    method,
    url,
    code: error?.code ?? null,
    responseMessage: typeof responseData?.message === "string" ? responseData.message : null,
    responseKeys: responseData && typeof responseData === "object" ? Object.keys(responseData) : [],
  };
};

export const captureAxiosError = (error, sentry = Sentry) => {
  if (!shouldCaptureAxiosError(error)) return false;

  const context = buildAxiosContext(error);

  sentry.withScope((scope) => {
    scope.setTag("error_source", "axios");

    if (context.status) scope.setTag("http_status", String(context.status));
    if (context.method) scope.setTag("http_method", context.method);
    if (context.code) scope.setTag("axios_code", context.code);
    if (context.url) scope.setExtra("request_url", context.url);

    scope.setContext("axios", context);
    sentry.captureException(error);
  });

  return true;
};
